import {
  LeadsListData,
  leadsListInfiniteOptions,
  leadsLogsListQueryKey,
  leadsMergePartialUpdateMutation,
  leadsRetrieveOptions,
  leadsRetrieveQueryKey,
  PaginatedReturnLeadListReadable,
  ReturnLeadReadable
} from '@crm/api'
import { InfiniteSelect } from '@crm/components'
import { OpportunityContext } from '@crm/contexts/OpportunityContext'
import { getCustomFields } from '@crm/utils/getCustomFields'
import { Text } from '@shared/components'
import { useInvalidatePredicateQueries } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors, transformOptions } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Col, Modal, notification, Row, Skeleton } from 'antd'
import { ModalProps } from 'antd/lib'
import {
  ArrowRight,
  Call,
  HierarchySquare3,
  ProfileCircle,
  Sms
} from 'iconsax-react'

import { MouseEvent, useContext, useEffect, useState } from 'react'

import { useParams } from 'react-router-dom'

import { InfoLeadCard } from './InfoLeadCard'

export const MergeLeadModal = (props: ModalProps) => {
  const { lead } = useContext(OpportunityContext)

  const [selectedLeadId, setSelectedLeadId] = useState<number>()

  const { t, currentLang } = useLocales()
  const { id, opportunityId } = useParams()
  const { invalidateQueries } = useInvalidatePredicateQueries()

  const { data: selectedLead, isLoading } = useQuery({
    ...leadsRetrieveOptions({
      path: { id: Number(selectedLeadId) }
    }),
    enabled: !opportunityId && !!selectedLeadId
  })

  useEffect(() => {
    if (!props.open) {
      setSelectedLeadId(undefined)
    }
  }, [props.open])

  const mutationConfig = {
    onSuccess: () => {
      invalidateQueries([
        leadsRetrieveQueryKey({ path: { id: Number(id) } }),
        leadsLogsListQueryKey({ path: { lead_id: String(id) } })
      ])

      props.onCancel?.({} as MouseEvent<HTMLButtonElement>)

      notification.success({
        message: t('success.success'),
        description: t('success.success_data_save')
      })
    },
    onError: (e: unknown) => showApiErrors(e)
  }
  const { mutate, isPending } = useMutation({
    ...leadsMergePartialUpdateMutation(),
    ...mutationConfig
  })

  const onSubmit = () => {
    if (selectedLeadId) {
      mutate({
        path: { id: Number(id) },
        body: { lead_to_merge: selectedLeadId }
      })
    }
  }

  const onChange = (id: number) => {
    if (opportunityId) {
      setSelectedLeadId(undefined)
    } else {
      setSelectedLeadId(id)
    }
  }

  const mergeLeadCustomFields = getCustomFields(
    selectedLead?.custom_field_values,
    t,
    currentLang
  )

  const currentLeadCustomFields = getCustomFields(
    lead?.custom_field_values,
    t,
    currentLang
  )

  return (
    <Modal
      title={t(
        opportunityId ? 'activities.merge_opportunity' : 'activities.merge_lead'
      )}
      cancelText={t('buttons.cancel')}
      okText={t('buttons.save')}
      onOk={onSubmit}
      centered
      width={700}
      okButtonProps={{
        loading: isPending,
        disabled: !selectedLeadId
      }}
      {...props}
    >
      <InfiniteSelect<
        LeadsListData,
        PaginatedReturnLeadListReadable,
        ReturnLeadReadable
      >
        value={selectedLeadId}
        popupMatchSelectWidth
        getQueryFunction={leadsListInfiniteOptions}
        placeholder={t('select')}
        loading={isLoading}
        getOptions={({ options }) =>
          transformOptions({
            options: options?.filter((option) => option?.id !== Number(id)),
            label: 'name',
            value: 'id',
            returnOption: true
          })
        }
        baseOptions={{
          query: {
            preference: selectedLeadId ? [Number(selectedLeadId)] : undefined
          }
        }}
        onChange={(value) => onChange(value)}
        className="w-full"
      />

      {isLoading && <Skeleton paragraph={{ rows: 4 }} />}

      {selectedLead && selectedLeadId && (
        <>
          <Row
            gutter={24}
            align="middle"
            justify="space-between"
            className="mt-20"
          >
            <Col span={10}>
              <InfoLeadCard
                loading={isLoading}
                leadDetails={[
                  {
                    icon: <ProfileCircle size={16} />,
                    label: selectedLead?.name || '-'
                  },
                  {
                    icon: <Sms size={16} />,
                    label: selectedLead?.contact?.email || '-'
                  },
                  {
                    icon: <Call size={16} />,
                    label:
                      selectedLead?.contact?.phone_numbers
                        ?.find(({ is_primary }) => is_primary)
                        ?.number?.toString() || '-'
                  },
                  ...(mergeLeadCustomFields ?? []).map((field) => ({
                    icon: <HierarchySquare3 size={16} />,
                    label: field.label
                  }))
                ]}
              />
            </Col>

            <ArrowRight size={36} />

            <Col span={10}>
              <InfoLeadCard
                leadDetails={[
                  {
                    icon: <ProfileCircle size={20} />,
                    label: lead?.name || ''
                  },
                  {
                    icon: <Sms size={20} />,
                    label: lead?.contact?.email || '-'
                  },
                  {
                    icon: <Call size={20} />,
                    label:
                      lead?.contact?.phone_numbers
                        ?.find(({ is_primary }) => is_primary)
                        ?.number?.toString() || '-'
                  },
                  ...(currentLeadCustomFields ?? []).map((field) => ({
                    icon: <HierarchySquare3 size={16} />,
                    label: field.label
                  }))
                ]}
              />
            </Col>
          </Row>

          <Row className="mt-20">
            <Col span={24} style={{ textAlign: 'center' }}>
              <Text type="danger" strong>
                {t('activities.missing_data_replacement', {
                  leadName: lead?.name,
                  dataName: selectedLead?.name
                })}
              </Text>
            </Col>
          </Row>
        </>
      )}
    </Modal>
  )
}
